import { ConfigProvider, Modal, Button, Divider, Flex } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { ModalHeader, modalButtonsTheme, modalSubtitleStyle } from "components/modals/ModalChrome";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatCurrency, GOODWILL_BOOKING_LABEL } from "shared/utils";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { tnum } from "shared/components/TableText";
import { localTzDate } from "shared/utils/dayjs";
import { SalesServiceBookingRow, SalesServicesRow } from "shared/types";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { textPx } from "shared/constants/zoom";

interface SessionDetailModalProps {
  session: SalesServicesRow | null;
  onClose: () => void;
}

// Plain value-over-label typography, no card/border/icon chip — these two
// numbers are a recap of what's already in the table below (Total Pendapatan
// = sum of "Nilai/Sesi", Peserta = row count), so they shouldn't compete for
// attention with the table the way a full MetricCard would.
const InlineStat = ({ value, label }: { value: string | number; label: string }) => (
  <div>
    <div
      style={{
        fontFamily: "var(--font-display)",
        fontSize: textPx(20),
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: colors.ink,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 2 }}>{label}</div>
  </div>
);

// Per-booking breakdown behind a session's aggregate revenue — lets a
// manager see WHY a session's total looks the way it does, e.g. an
// unlimited-membership attendee correctly contributing Rp 0 (that cash was
// already counted under "Penjualan Paket" when the membership was sold).
const SessionDetailModal = ({ session, onClose }: SessionDetailModalProps) => {
  // modalSubtitleStyle is a BLOCK subtitle (display: block + marginTop: 8,
  // for the line under a modal title). Inside the flex meta row that margin
  // shifts the text's margin box, so align="center" leaves the text ~4px
  // lower than the icon. Reuse the typography but drop the block layout.
  const inlineSubtitleStyle = {
    ...modalSubtitleStyle,
    display: "inline",
    marginTop: 0,
  } as const;

  const columns: ReportColumn<SalesServiceBookingRow>[] = [
    reportColumn<SalesServiceBookingRow>("client", {
      dataIndex: "customer_name",
      // Klien keeps a fixed share here: the product name is the long text of
      // this table, so that column is the one that stretches.
      kind: "text",
      share: 22,
      min: 170,
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
      extra: { ellipsis: true },
    }),
    reportColumn<SalesServiceBookingRow>("booking_code", {
      dataIndex: "booking_ref",
      render: (value: string | null) => (
        <span style={{ ...tnum, color: colors.ink2 }}>{value ?? "-"}</span>
      ),
    }),
    reportColumn<SalesServiceBookingRow>("product", {
      dataIndex: "package_name",
      kind: "name",
      render: (value: string) => value,
      extra: { ellipsis: true },
    }),
    reportColumn<SalesServiceBookingRow>("credit_cost", {
      dataIndex: "credit_cost",
      // A membership takes no credits (its remaining_credits is null).
      render: (value: number | undefined, row: SalesServiceBookingRow) => (
        <span style={{ ...tnum, color: colors.ink2 }}>
          {row.remaining_credits === null ? "-" : (value ?? 1)}
        </span>
      ),
    }),
    reportColumn<SalesServiceBookingRow>("remaining_credits", {
      dataIndex: "remaining_credits",
      // null = unlimited membership (no credit pool to count down).
      render: (value: number | null) => (
        <span style={{ ...tnum, color: colors.ink2 }}>
          {value === null ? translate("customer_package.unlimited") : value}
        </span>
      ),
    }),
    reportColumn<SalesServiceBookingRow>("booking_revenue", {
      dataIndex: "revenue",
      // A goodwill booking (credit past the package's paid pool) reads Rp 0
      // by rule; the chip says why.
      render: (value: number, row: SalesServiceBookingRow) => (
        <Flex align="center" justify="flex-end" gap={6}>
          {row.goodwill && <StatusBadge tone="neutral" label={GOODWILL_BOOKING_LABEL} />}
          <span style={{ ...tnum, fontWeight: 600 }}>{formatCurrency(value)}</span>
        </Flex>
      ),
    }),
  ];

  return (
    <ConfigProvider theme={modalButtonsTheme}>
      <Modal
        open={!!session}
        onCancel={onClose}
        title={null}
        // Widened from 920 to fit the added Kode Booking column (130px)
        // without cutting off Nilai/Sesi at the right edge, then to 1110 for
        // the Kredit column.
        width={1110}
        styles={{ body: { paddingTop: 4 } }}
        footer={
          <Button onClick={onClose}>{translate("button.close")}</Button>
        }
      >
        {session && (
          <>
            <ModalHeader
              eyebrow={translate("analyticsPage.sales.session_detail_eyebrow")}
              title={session.class_name}
            />
            <Flex align="center" justify="space-between" wrap="wrap" gap={16} style={{ marginTop: 12 }}>
              <Flex align="center" gap={8}>
                <span style={inlineSubtitleStyle}>
                  {localTzDate(session.start_time).format("DD MMM YYYY, HH:mm")}
                </span>
                <span style={{ color: colors.lineStrong }}>·</span>
                <Flex align="center" gap={5}>
                  <UserOutlined style={{ fontSize: textPx(12), color: colors.ink3 }} />
                  <span style={inlineSubtitleStyle}>{session.instructor_name}</span>
                </Flex>
              </Flex>
              <Flex gap={28}>
                <InlineStat
                  value={formatCurrency(session.revenue)}
                  label={translate("analyticsPage.sales.kpi_total_revenue")}
                />
                <InlineStat
                  value={session.attendees}
                  label={translate("analyticsPage.columns.attendees")}
                />
              </Flex>
            </Flex>
            <Divider style={{ margin: "20px 0" }} />
            <div style={{ fontWeight: 600, fontSize: textPx(15), color: colors.ink }}>
              {translate("analyticsPage.sales.session_detail_heading")}
            </div>
            <div style={{ fontSize: textPx(13), color: colors.ink3, marginTop: 2, marginBottom: 14 }}>
              {translate("analyticsPage.sales.session_detail_description")}
            </div>
            <ReportTable<SalesServiceBookingRow>
              rowKey="id"
              size="small"
              sticky={false}
              // Its two ellipsis columns need fixed percentage widths; the
              // content layout would simply widen them.
              layout="shares"
              columns={columns}
              dataSource={session.bookings}
              pagination={false}
              scrollY={session.bookings.length > 8 ? 360 : undefined}
            />
          </>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default SessionDetailModal;
